import { useQuery, useMutation } from 'convex/react';
import { api } from '../../api';
import { useUserDevice } from './useUserDevice';

type BrowserModeBackend = 'none' | 'categories' | 'whitelist';
type BrowserModeUI = 'no-browser' | 'filtered-browser' | 'whitelisted-browser';

const uiToBackend: Record<BrowserModeUI, BrowserModeBackend> = {
  'no-browser': 'none',
  'filtered-browser': 'categories',
  'whitelisted-browser': 'whitelist',
};

const backendToUI: Record<BrowserModeBackend, BrowserModeUI> = {
  none: 'no-browser',
  categories: 'filtered-browser',
  whitelist: 'whitelisted-browser',
};

/**
 * Infer browser mode from blueJayRules when no explicit browserMode field exists.
 */
const inferBrowserMode = (blueJayRules: any): BrowserModeBackend => {
  if (blueJayRules === false || blueJayRules === undefined || blueJayRules === null) {
    return 'none';
  }

  // If all categories are blocked → whitelist mode
  if (typeof blueJayRules === 'object' && blueJayRules !== null) {
    const categories = blueJayRules.categories;
    if (Array.isArray(categories) && categories.length > 0) {
      // If there are many blocked categories, it's likely whitelist mode
      // But we can't be sure without knowing all categories, so default to categories
    }
  }

  return 'categories';
};

export const useDeviceBrowserMode = () => {
  const { device, deviceConfig, isLoading: isDeviceLoading } = useUserDevice();
  const updatePolicies = useMutation(api.devices.policies.update);

  const deviceId = device?._id;

  // Read policies from the correct endpoint
  const shouldSkip = isDeviceLoading || !deviceConfig;
  const policiesData = useQuery(
    api.devices.policies.get,
    !shouldSkip ? { deviceConfig: deviceConfig as any } : "skip"
  );

  // Read explicit browserMode, fall back to inference
  const explicitMode = (policiesData as any)?.browserMode as BrowserModeBackend | undefined;
  const inferredMode = policiesData ? inferBrowserMode(policiesData.blueJayRules) : 'none';
  const currentModeBackend = explicitMode ?? inferredMode;
  const currentMode: BrowserModeUI = backendToUI[currentModeBackend] ?? 'no-browser';

  const setBrowserMode = async (uiModeId: string) => {
    if (!deviceId) throw new Error('No device');
    const backendMode = uiToBackend[uiModeId as BrowserModeUI];
    if (!backendMode) throw new Error(`Unknown UI mode: ${uiModeId}`);

    await updatePolicies({ device: deviceId as any, browserMode: backendMode } as any);
  };

  return {
    currentMode,
    isLoading: isDeviceLoading || (!shouldSkip && policiesData === undefined),
    setBrowserMode,
  };
};
